import styled, { useTheme } from 'styled-components';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Heading1Text,
  Heading2Text,
  ParagraphSmallText,
} from '../../../../components/Texts';
import WidthContainer from '../../../../components/WidthContainer';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive';
import { selectThemeMode } from '../../../../redux/slices/themeSlice';
import { useSelector } from 'react-redux';

const BackgroundColor = styled.div`
  background-color: ${({ theme }) => theme.primary.background};
  position: absolute;
  width: 100%;
  left: 0px;
`;

const Container = styled.div`
  min-height: 700px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  @media ${PHONE_MEDIA_QUERY} {
    min-height: unset;
    text-align: center;
    padding-top: 120px;
    padding-bottom: 120px;
  }
`;

const Card = styled.img`
  border-radius: 20px;
  width: 400px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 80vw;
  }
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
`;

const Carousel = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  gap: 30px;
  overflow-x: auto;
  padding-left: calc((100vw - 1279px) / 2);
  padding-right: calc((100vw - 1279px) / 2);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media ${PHONE_MEDIA_QUERY} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  gap: 10px;
`;

const CardDescription = styled(ParagraphSmallText)`
  font-weight: 600;
`;

const UsageIdeas = () => {
  const themeMode = useSelector(selectThemeMode);
  const backgroundRef = useRef(null);
  const carouselRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - carouselRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      carouselRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );
  const theme = useTheme();

  return (
    <>
      <BackgroundColor ref={backgroundRef}>
        <Container>
          <WidthContainer>
            <Heading2Text style={{ color: theme.text.title }}>
              Gift Card{' '}
              <span style={{ color: theme.special.purple }}>Usage Ideas</span>
            </Heading2Text>
          </WidthContainer>
          <Carousel
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            <CardContainer>
              <Card
                src={
                  themeMode === 'light'
                    ? '/landing-v2/usage/birthday.webp'
                    : '/landing-v2/usage/birthday-dark.webp'
                }
              />
              <CardDescription>Birthday</CardDescription>
            </CardContainer>
            <CardContainer>
              <Card
                src={
                  themeMode === 'light'
                    ? '/landing-v2/usage/corporate.webp'
                    : '/landing-v2/usage/corporate-dark.webp'
                }
              />
              <CardDescription>Corporate gifts</CardDescription>
            </CardContainer>
            <CardContainer>
              <Card
                src={
                  themeMode === 'light'
                    ? '/landing-v2/usage/appreciation.webp'
                    : '/landing-v2/usage/appreciation-dark.webp'
                }
              />
              <CardDescription>Appreciation</CardDescription>
            </CardContainer>
            <CardContainer>
              <Card
                src={
                  themeMode === 'light'
                    ? '/landing-v2/usage/investment.webp'
                    : '/landing-v2/usage/investment-dark.webp'
                }
              />
              <CardDescription>Investment starter</CardDescription>
            </CardContainer>
            <CardContainer>
              <Card
                src={
                  themeMode === 'light'
                    ? '/landing-v2/usage/any.webp'
                    : '/landing-v2/usage/any-dark.webp'
                }
              />
              <CardDescription>Any occasion</CardDescription>
            </CardContainer>
          </Carousel>
        </Container>
      </BackgroundColor>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default UsageIdeas;
